import React, { useEffect, useState } from 'react';
import MainLayout from '../layouts/MainLayout';
import { FacteurCouvertureSolaireParZoneClimatiqueDto, ParZoneClimatique } from "../interfaces/AbaqueType";
import { getAbaques, getTypeAbaques, importeAbaqueData } from "../auth";
import {
    Typography,
    Box,
    Card,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    SelectChangeEvent
} from "@mui/material";
import { CloudUpload } from '@mui/icons-material';

const AbaquesManagement: React.FC = () => {
    const [abaqueTypes, setAbaqueTypes] = useState<string[]>([]);
    const [selectedAbaqueType, setSelectedAbaqueType] = useState<string>('');
    const [parZoneClimatique, setParZoneClimatique] = useState<ParZoneClimatique[]>([]);
    const [facteurCouvertureSolaireParZoneClimatique, setFacteurCouvertureSolaireParZoneClimatique] = useState<FacteurCouvertureSolaireParZoneClimatiqueDto[]>([]);
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    useEffect(() => {
        fetchTypesAbaques();
    }, []);

    const fetchTypesAbaques = async () => {
        try {
            const response = await getTypeAbaques() as string[];
            setAbaqueTypes(response);
        } catch (error) {
            setError('Erreur lors de la récupération des types d\'abaques');
            console.error('Erreur:', error);
        }
    };

    const fetchAbaqueData = async (type: string) => {
        setLoading(true);
        setError(null);
        try {
            setParZoneClimatique([]);
            setFacteurCouvertureSolaireParZoneClimatique([]);
            if (type === 'facteurCouvertureSolaire') {
                const response = await getAbaques(type) as FacteurCouvertureSolaireParZoneClimatiqueDto[];
                setFacteurCouvertureSolaireParZoneClimatique(response);
            } else {
                const response = await getAbaques(type) as ParZoneClimatique[];
                response.sort((a, b) => a.indMois - b.indMois);
                setParZoneClimatique(response);
            }
        } catch (error) {
            setError('Erreur lors de la récupération des abaques');
            console.error('Erreur:', error);
        }
        setLoading(false);
    };

    const handleAbaqueTypeChange = (event: SelectChangeEvent<string>) => {
        const type = event.target.value;
        setSelectedAbaqueType(type);
        if (type) {
            fetchAbaqueData(type);
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            setFile(event.target.files[0]);
        }
    };

    const handleFileUpload = async () => {
        if (!file || !selectedAbaqueType) {
            setError('Veuillez sélectionner un type d\'abaque et un fichier.');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            await importeAbaqueData(file, selectedAbaqueType);
            setSuccess('Fichier importé avec succès !');
            setFile(null);
            fetchAbaqueData(selectedAbaqueType);
        } catch (error) {
            setError('Erreur lors de l\'importation du fichier');
            console.error('Erreur:', error);
        }
        setLoading(false);
    };

    return (
        <MainLayout>
            <Box className="p-6 space-y-6">
                {/* En-tête */}
                <Card className="bg-white/80 backdrop-blur-sm p-6">
                    <Typography variant="h4" className="text-emerald-800 font-bold mb-4">
                        Gestion des abaques
                    </Typography>

                    {/* Sélecteur de type d'abaque */}
                    <FormControl fullWidth className="mb-6">
                    <InputLabel id="abaque-type-label">Type d'abaque</InputLabel>
                    <Select
                        labelId="abaque-type-label"
                        id="abaque-type-select"
                        value={selectedAbaqueType}
                        label="Type d'abaque"
                        onChange={handleAbaqueTypeChange}
                        className="bg-white"
                    >
                        <MenuItem value="">-- Sélectionner un type d'abaque --</MenuItem>
                        {abaqueTypes.map((type) => (
                            <MenuItem key={type} value={type}>
                                {type}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                    {/* Messages d'état */}
                    {error && (
                        <Alert severity="error" className="mb-4">
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert severity="success" className="mb-4">
                            {success}
                        </Alert>
                    )}

                    {/* Zone d'import de fichier */}
                    <Card className="p-6 border-2 border-dashed border-emerald-200 bg-emerald-50/50">
                        <div className="flex flex-col items-center space-y-4">
                            <input
                                type="file"
                                accept=".xlsx, .xls"
                                onChange={handleFileChange}
                                className="hidden"
                                id="file-upload"
                            />
                            <label htmlFor="file-upload" className="cursor-pointer">
                                <Button
                                    component="span"
                                    variant="outlined"
                                    startIcon={<CloudUpload />}
                                    className="text-emerald-600 border-emerald-600 hover:bg-emerald-50"
                                >
                                    Sélectionner un fichier Excel
                                </Button>
                            </label>
                            {file && (
                                <Typography className="text-emerald-600">
                                    Fichier sélectionné : {file.name}
                                </Typography>
                            )}
                            <Button
                                onClick={handleFileUpload}
                                variant="contained"
                                disabled={!file || !selectedAbaqueType || loading}
                                className="bg-emerald-600 hover:bg-emerald-700"
                            >
                                {loading ? <CircularProgress size={24} /> : 'Importer le fichier'}
                            </Button>
                        </div>
                    </Card>
                </Card>

                {/* Tableaux de données */}
                {loading ? (
                    <div className="flex justify-center p-8">
                        <CircularProgress className="text-emerald-600" />
                    </div>
                ) : (
                    <>
                        {/* Tableau Par Zone Climatique */}
                        {parZoneClimatique.length > 0 && (
                            <TableContainer component={Paper} className="bg-white/80 backdrop-blur-sm">
                                <Table>
                                    <TableHead>
                                        <TableRow className="bg-emerald-50">
                                            <TableCell>Mois</TableCell>
                                            <TableCell>h1a</TableCell>
                                            <TableCell>h1b</TableCell>
                                            <TableCell>h1c</TableCell>
                                            <TableCell>h2a</TableCell>
                                            <TableCell>h2b</TableCell>
                                            <TableCell>h2c</TableCell>
                                            <TableCell>h2d</TableCell>
                                            <TableCell>h3</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {parZoneClimatique.map((data) => (
                                            <TableRow key={data.indMois} hover>
                                                <TableCell>{data.mois}</TableCell>
                                                <TableCell>{data.h1a}</TableCell>
                                                <TableCell>{data.h1b}</TableCell>
                                                <TableCell>{data.h1c}</TableCell>
                                                <TableCell>{data.h2a}</TableCell>
                                                <TableCell>{data.h2b}</TableCell>
                                                <TableCell>{data.h2c}</TableCell>
                                                <TableCell>{data.h2d}</TableCell>
                                                <TableCell>{data.h3}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {/* Tableau Facteur Couverture Solaire */}
                        {facteurCouvertureSolaireParZoneClimatique.length > 0 && (
                            <TableContainer component={Paper} className="bg-white/80 backdrop-blur-sm mt-6">
                                <Table>
                                    <TableHead>
                                        <TableRow className="bg-emerald-50">
                                            <TableCell>Zone Climatique</TableCell>
                                            <TableCell>Chauffage solaire</TableCell>
                                            <TableCell>ECS solaire seule sup 5 ans</TableCell>
                                            <TableCell>ECS solaire seule inf ou égale 5 ans</TableCell>
                                            <TableCell>Chauffage + ECS solaire</TableCell>
                                            <TableCell>ECS solaire seule sup 5 ans</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {facteurCouvertureSolaireParZoneClimatique.map((data) => (
                                            <TableRow key={data.zoneClimatique} hover>
                                                <TableCell>{data.zoneClimatique}</TableCell>
                                                <TableCell>{data.maisonFchChauffageSolaire}</TableCell>
                                                <TableCell>{data.maisonFecsMoins5Ans}</TableCell>
                                                <TableCell>{data.immeubleCollectifFecsInf5Ans}</TableCell>
                                                <TableCell>{data.immeubleCollectifFecsChauffageEcsSolaire}</TableCell>
                                                <TableCell>{data.immeubleCollectifFecsSolaireSeuleSup5Ans}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                )}
            </Box>
        </MainLayout>
    );
};

export default AbaquesManagement;
